import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { NewsCard } from "@/components/NewsCard";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Newspaper } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// V9.21 — Sprint UX Simplification : taxonomie publique réduite à 6 groupes.
// Les catégories techniques du backend restent inchangées ; on filtre côté client
// via ces groupes plus lisibles pour le grand public.
// "À venir" n'apparaît plus dans les actualités (voir /calendrier).
const GROUPS = [
  { v: "all",         label: "Tout",                     cats: null /* pas de filtre */ },
  { v: "news",        label: "Actualités",               cats: ["actualite", "annonce", "communique", "article", "billet_contributeur", "autre"] },
  { v: "results",     label: "Résultats",                cats: ["resultat", "resultats", "retour_competition"] },
  { v: "portraits",   label: "Portraits & Interviews",   cats: ["portrait", "interview"] },
  { v: "structures",  label: "Clubs & Fédérations",      cats: ["communique_club", "communique_federation"] },
  { v: "medias",      label: "Médias",                   cats: ["video", "galerie", "reportage_photo", "live"] },
];

function matchesGroup(item, group) {
  if (!group || group === "all") return true;
  const g = GROUPS.find((x) => x.v === group);
  if (!g || !g.cats) return true;
  const cat = item.category || item.type;
  return cat && g.cats.includes(cat);
}

export default function NewsListPage() {
  const [group, setGroup] = useState("all");
  const [discipline, setDiscipline] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])); }, []);

  useEffect(() => {
    setLoading(true);
    // V9.21 — On ne pousse plus la catégorie au backend : le filtrage se fait par
    // groupe côté client (taxonomie publique simplifiée). La discipline reste au backend.
    const params = { status: "published", limit: 200 };
    if (discipline !== "all") params.discipline = discipline;
    const t = setTimeout(() => {
      Promise.all([
        api.get("/curator/items", { params }).catch(() => ({ data: { items: [] } })),
        // V9.11 — Articles contributeurs publiés mergés dans le feed
        api.get("/contrib/articles/public", {
          params: { limit: 50, ...(discipline !== "all" ? { discipline } : {}) },
        }).catch(() => ({ data: { items: [] } })),
      ])
        .then(([curr, contrib]) => {
          const dateOf = (it) => new Date(it.published_at || it.date || it.created_at || 0).getTime();
          const merged = [...(curr.data.items || []), ...(contrib.data.items || [])]
            .sort((x, y) => dateOf(y) - dateOf(x));
          setItems(merged);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [discipline]);

  // Filtrage groupe + texte côté client
  const filteredItems = useMemo(() => {
    const byGroup = items.filter((it) => matchesGroup(it, group));
    if (!q.trim()) return byGroup;
    const needle = q.toLowerCase();
    return byGroup.filter((it) =>
      (it.title || "").toLowerCase().includes(needle)
      || (it.summary || "").toLowerCase().includes(needle)
      || (it.source_name || "").toLowerCase().includes(needle)
      || (it.tagged_athletes || []).some((n) => (n || "").toLowerCase().includes(needle))
    );
  }, [items, q, group]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Actualités</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Le fil sportif du Fenua — articles curatés, contributions des sportifs et clubs. Filtre par catégorie, discipline ou recherche.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-b pb-4" data-testid="news-filters">
        <Tabs value={group} onValueChange={setGroup} data-testid="news-filters-tabs">
          <TabsList className="flex-wrap h-auto">
            {GROUPS.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`news-filter-${t.v}`} className="text-xs">{t.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="grid sm:grid-cols-2 gap-3">
          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger data-testid="news-filters-discipline-select"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les disciplines</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="news-filters-search-input" placeholder="Rechercher dans les actualités…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
        ) : filteredItems.length === 0 ? (
          <EmptyState icon={Newspaper} title="Aucune actualité pour ce filtre." description="Essaye d'élargir tes critères ou reviens plus tard." />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((n) => <NewsCard key={n.id} item={n} />)}
          </div>
        )}
      </div>
    </div>
  );
}
